import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { SxProps, Theme } from '@mui/material/styles';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { useDashboardPageScroll } from 'src/common/components/dashboard/DashboardPageScrollContext';
import { dashboardScrollContainerSx } from 'src/common/components/dashboardPageLayout';
import { DashboardGridChrome } from 'src/common/components/dataGrid/useDashboardGridHeight';
import { ticketCardGridContainerPaddingSx, ticketCardGridItemSx, ticketCardGridSize } from 'src/common/styles/ticketCardStyles';

import { chunkIntoRows, useResponsiveColumnCount } from './useResponsiveColumnCount';

const GRID_SPACING_PX = 16;
const DEFAULT_ESTIMATE_ROW_HEIGHT = 360;

type Props<T> = {
  items: T[];
  renderItem: (item: T) => ReactNode;
  getItemKey: (item: T) => string;
  chrome?: DashboardGridChrome;
  estimateRowHeight?: number;
  /** Renders above the cards inside the scroll flow (scrolls out of view). */
  header?: ReactNode;
  'data-testid'?: string;
};

function offsetWithinScrollParent(element: HTMLElement, scrollParent: HTMLElement): number {
  return (
    element.getBoundingClientRect().top -
    scrollParent.getBoundingClientRect().top +
    scrollParent.scrollTop
  );
}

function findOverflowScrollParent(start: HTMLElement | null): HTMLElement | null {
  let node = start;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

function VirtualizedCardGrid<T>({
  items,
  renderItem,
  getItemKey,
  chrome: _chrome = 'standard',
  estimateRowHeight = DEFAULT_ESTIMATE_ROW_HEIGHT,
  header,
  'data-testid': dataTestId = 'virtualized-card-grid',
}: Props<T>) {
  const pageScroll = useDashboardPageScroll();
  const localScrollRef = useRef<HTMLDivElement>(null);
  const virtualListRef = useRef<HTMLDivElement>(null);
  const pageScrollRef = pageScroll?.scrollRef;
  const useParentScroll = Boolean(pageScrollRef);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const boundScrollElementRef = useRef<HTMLElement | null>(null);

  const columnCount = useResponsiveColumnCount();
  const rows = chunkIntoRows(items, columnCount);

  // Bind the scroll element after commit. useVirtualizer reads getScrollElement during
  // render, when parent scrollRef.current can still be null on the first paint of a
  // Resolver-mounted page — leaving outerSize at 0 and rendering no rows until a
  // later re-render (e.g. changing the edition filter) rebinds it.
  useLayoutEffect(() => {
    let resizeObserver: ResizeObserver | undefined;
    let rafId = 0;

    const measureMargin = () => {
      const scrollEl = boundScrollElementRef.current;
      const listEl = virtualListRef.current;
      if (!scrollEl || !listEl) {
        setScrollMargin(0);
        return;
      }
      setScrollMargin(offsetWithinScrollParent(listEl, scrollEl));
    };

    const clearObserver = () => {
      resizeObserver?.disconnect();
      resizeObserver = undefined;
      window.removeEventListener('resize', measureMargin);
    };

    const resolveScrollElement = (): HTMLElement | null => {
      if (!useParentScroll) {
        return localScrollRef.current;
      }
      return (
        pageScrollRef?.current ??
        findOverflowScrollParent(virtualListRef.current?.parentElement ?? null)
      );
    };

    const bind = (): boolean => {
      const scrollEl = resolveScrollElement();
      boundScrollElementRef.current = scrollEl;
      setScrollElement(scrollEl);
      clearObserver();

      if (!useParentScroll) {
        setScrollMargin(0);
        return Boolean(scrollEl);
      }

      const listEl = virtualListRef.current;
      if (!scrollEl || !listEl) {
        setScrollMargin(0);
        return false;
      }

      measureMargin();
      resizeObserver = new ResizeObserver(measureMargin);
      resizeObserver.observe(scrollEl);
      resizeObserver.observe(listEl);
      window.addEventListener('resize', measureMargin);
      return true;
    };

    if (!bind()) {
      // Parent scrollRef is sometimes still unset in this layout pass; retry next frame.
      rafId = requestAnimationFrame(() => {
        bind();
      });
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      clearObserver();
    };
  }, [useParentScroll, pageScrollRef, header, items.length, columnCount]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => estimateRowHeight,
    gap: GRID_SPACING_PX,
    overscan: 1,
    scrollMargin: useParentScroll ? scrollMargin : 0,
    enabled: Boolean(scrollElement) && rows.length > 0,
  });

  const rootSx = (
    useParentScroll
      ? [{ width: '100%' }, ticketCardGridContainerPaddingSx]
      : [
          {
            width: '100%',
            flex: '1 1 0',
            minHeight: 0,
            overflowY: 'auto',
          },
          dashboardScrollContainerSx,
          ticketCardGridContainerPaddingSx,
        ]
  ) as SxProps<Theme>;

  return (
    <Box ref={useParentScroll ? undefined : localScrollRef} data-testid={dataTestId} sx={rootSx}>
      {header ? <Box sx={{ mb: 2, width: '100%' }}>{header}</Box> : null}
      <Box
        ref={virtualListRef}
        sx={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map(virtualRow => {
          const rowItems = rows[virtualRow.index] ?? [];
          return (
            <Box
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start - (useParentScroll ? scrollMargin : 0)}px)`,
                overflow: 'visible',
              }}
            >
              <Grid container spacing={2} sx={{ overflow: 'visible' }}>
                {rowItems.map(item => (
                  <Grid
                    key={getItemKey(item)}
                    size={ticketCardGridSize}
                    sx={ticketCardGridItemSx}
                  >
                    {renderItem(item)}
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default VirtualizedCardGrid;
